import { ExclamationCircleIcon } from "@heroicons/react/outline";
import * as Sentry from "@sentry/react";
import { useState } from "react";

import {
  LISBlock,
  LISCaseList,
  LISCaseListBlock,
  LISLabOrder,
  LISSlide,
  LISWork,
  Stain,
} from "pwapi";

import { ExternalLabList, StainList } from "custom-models/frontLoader";

import { useRequestAfterEverySiteLoad } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  mapReasonCodeToTitle,
  normalizeOrderNotes,
} from "shared/utils/flaggingUtils";
import { pluralize } from "shared/utils/helpers";

import { listCases } from "app/actions/caseActions";
import { useAppSelector } from "app/hooks";
import { workWithTaskIn } from "app/selectors/workSelectors";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import useCuttingOnScanCallbacks from "hooks/useCuttingOnScanCallbacks";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";
import AppSpinner from "ui/components/AppSpinner";
import { UserFullNameDisplay } from "ui/components/UserFullName";
import CaseListItem from "ui/lists/CaseListItem";
import CaseReadyItem from "ui/lists/CaseReadyItem";
import { labelForBlock } from "util/blockUtils";
import { getOrderFlagInfo } from "util/getOrderFlagInfo";
import { NO_CUTTING_SLIDES, NO_CUTTING_WORK } from "util/sentryEvents";
import { labelForSlide } from "util/slideUtils";
import { blockSort, specimenSort } from "util/sortFunctions";
import { workProcessSort } from "util/workProcessUtils";

import { SlideFlagIcon } from "../ui/SlideFlagIcon";
import TaskCount from "../ui/TaskCount";

const Block = ({
  block,
  pathCase,
  onScanBlock,
}: {
  block: LISBlock;
  pathCase: LISCaseList;
  onScanBlock: (id: number) => void;
}) => {
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  return (
    <article title={`${block.id}`} className="CuttingStation__block">
      <h3
        className="Scannable Label"
        onClick={(e) => {
          // TODO: this should be abstracted
          if (lisAltClickScanning && e.altKey && block.id) {
            onScanBlock(block.id);
          }
        }}
      >
        {labelForBlock(block, pathCase)}
      </h3>
    </article>
  );
};

const blockFilter =
  (work: LISWork, pathCase: LISCaseList) =>
  (b: LISBlock): boolean => {
    if (!work || !pathCase) return false;
    if (work.order && work.order.block_id !== b.id) return false;

    const workSlides = pathCase.slides.filter(
      (s) =>
        s.block_obj_id === b.id &&
        (!work.order || s.lab_order_id === work.order.id)
    );

    return (
      workSlides.length === 0 ||
      workSlides.some((s) => s.lis_state === "unstained")
    );
  };

const Order = ({
  work,
  onScanBlock,
}: {
  work: LISWork;
  onScanBlock: (id: number) => void;
}) => {
  const order = work.order;

  const pathCase = useAppSelector((state) =>
    Object.values(state.casesWithWork).find((c) =>
      c.work_processes.some((w) => w.id === work.id)
    )
  );

  const orderLabStain = useAppSelector(
    ({ labStains }) => labStains[order?.lab_stain_id || 0]
  );
  const orderStain = useAppSelector(
    ({ stains }) => stains[orderLabStain?.stain_id || 0]
  );
  const stain = orderStain || {
    id: 1,
    name: "H&E",
    abbreviation: "H&E",
    category: "ROUTINE",
  }; // Use labStain if present

  const externalStain = useAppSelector(
    ({ externalLabStains }) =>
      externalLabStains[orderLabStain?.external_lab_stain_id || 0]
  );
  const externalLab = useAppSelector(
    ({ externalLabs }) => externalLabs[externalStain?.external_lab_id || 0]
  );

  const block = pathCase?.blocks.find((b) => b.id === order?.block_id);

  if (!order || !pathCase || !block) return null;

  return (
    <OrderView
      {...{
        block,
        externalLab,
        onScanBlock,
        order,
        pathCase,
        stain,
      }}
    />
  );
};

interface OrderViewProps {
  block: LISCaseListBlock;
  externalLab?: ExternalLabList;
  onScanBlock: (id: number) => void;
  order?: LISLabOrder;
  pathCase: LISCaseList;
  stain: StainList;
}
export const OrderView: React.FC<OrderViewProps> = ({
  block,
  externalLab,
  onScanBlock,
  order,
  pathCase,
  stain,
}) => {
  const { flagReasonCode = "", flagNote } = getOrderFlagInfo(order, pathCase);
  const orderNote = order?.note;
  const normalizedNotes = normalizeOrderNotes(orderNote, flagNote);

  return (
    <>
      <div>
        <code>
          <strong>{stain.name}</strong>
          {stain.name !== stain.abbreviation && <> ({stain.abbreviation})</>}
        </code>
      </div>
      <div>
        <code>&nbsp;{stain.category}</code>
      </div>
      <div>
        {externalLab && (
          <span>
            <code>Send-Out</code>: <code>{externalLab.name}</code>
          </span>
        )}
      </div>
      <div className="CuttingStation__blocks">
        <Block block={block} onScanBlock={onScanBlock} pathCase={pathCase} />
      </div>
      {!!order &&
        (order.user_id || flagReasonCode || !!normalizedNotes.length) && (
          <div className="WorkList__item__order">
            {!!order.user_id && (
              <span className="WorkList__item__order__user">
                <UserFullNameDisplay
                  first_name={order?.user?.first_name}
                  last_name={order?.user?.last_name}
                  credentials={order?.user?.userprofile?.credentials}
                  showCredentials={true}
                />
              </span>
            )}
            {!!flagReasonCode && (
              <div className="WorkList__item__order__reason">
                <SlideFlagIcon /> {mapReasonCodeToTitle(flagReasonCode)}
              </div>
            )}
            {normalizedNotes.map((note) => (
              <div key={note} className="WorkList__item__order__note">
                {note}
              </div>
            ))}
          </div>
        )}
    </>
  );
};

export const RoutineWork = ({
  pathCase,
  order,
  stainId,
  blockIds,
  onScanBlock,
}: {
  pathCase: LISCaseList;
  stainId: number;
  order?: LISLabOrder;
  blockIds: number[];
  onScanBlock: (id: number) => void;
}) => {
  const stain = useAppSelector(({ stains }) => stains[stainId]);
  const blocks = pathCase.blocks.filter((block) =>
    blockIds.includes(block.id || 0)
  );
  const specimenIds = blocks.map((block) => block.specimen_id);
  const specimens = pathCase.specimens
    .filter((specimen) => specimenIds.includes(specimen.id || 0))
    .sort(specimenSort);
  const orderNote = order?.note;
  const { flagReasonCode = "", flagNote } = getOrderFlagInfo(order, pathCase);
  const orderNotes = normalizeOrderNotes(orderNote, flagNote);

  return (
    <>
      <div>
        <code>
          <strong>{stain.name}</strong>
          {stain.name !== stain.abbreviation && <> ({stain.abbreviation})</>}
        </code>
      </div>
      <div>
        <code>&nbsp;{stain.category}</code>
      </div>
      <div></div>
      <div className="CuttingStation__blocks">
        {specimens.map((s) =>
          blocks
            .filter((b) => b.specimen_id === s.id)
            .sort(blockSort)
            .map((b) => (
              <Block
                block={b}
                key={b.id}
                onScanBlock={onScanBlock}
                pathCase={pathCase}
              />
            ))
        )}
      </div>
      {!!orderNotes.length && (
        <div className="WorkList__item__order RoutineWork">
          {!!flagReasonCode && (
            <div className="WorkList__item__order__reason">
              <SlideFlagIcon /> {mapReasonCodeToTitle(flagReasonCode)}
            </div>
          )}
          {orderNotes.map((note) => (
            <div key={note} className="WorkList__item__order__note">
              {note}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const Case = ({
  pathCase,
  onScanBlock,
  category,
  filterBySendout,
}: {
  pathCase: LISCaseList;
  onScanBlock: (id: number) => void;
  category?: Stain.category;
  filterBySendout?: string;
}) => {
  if (!pathCase.id) return null;

  const labStains = useAppSelector(({ labStains }) => labStains);
  const stains = useAppSelector(({ stains }) => stains);

  const blocks = pathCase.blocks;

  const work = pathCase.work_processes.filter((w) =>
    w.tasks.some((t) => t.type === "cutting" && !t.completed_at)
  );

  if (!work.length) {
    Sentry.captureEvent({
      event_id: NO_CUTTING_WORK,
      message:
        "Tried to render a Case at the Cutting Station which has no Cutting Task",
    });

    return null;
  }

  if (!work.some((w) => blocks.some(blockFilter(w, pathCase)))) {
    Sentry.captureEvent({
      event_id: NO_CUTTING_SLIDES,
      message:
        "Tried to render a Case at the Cutting Station which has no uncut Blocks or unstained Slides",
    });

    return null;
  }

  const filterCase =
    category || filterBySendout
      ? !!work?.filter((w) => {
          const labStain = labStains[w.order?.lab_stain_id || 0];
          const stain = stains[labStain?.stain_id || 0];

          const filteredBySendout =
            !filterBySendout ||
            (filterBySendout === "inhouse" &&
              !labStain?.external_lab_stain_id) ||
            (filterBySendout === "sendout" && labStain?.external_lab_stain_id);

          const filterByCategory =
            !category ||
            [category as string, `PW-${category}`].includes(
              stain?.category || "ROUTINE"
            );
          return filteredBySendout && filterByCategory;
        }).length
      : true;

  const routineWork: LISWork[] = [];
  const nonRoutineWork: LISWork[] = [];

  type BlocksOrderByStain = {
    [key: number]: { blockIds: number[]; order?: LISLabOrder };
  };
  const routineBlocksOrderByStain: BlocksOrderByStain = {};

  work.forEach((w) => {
    const labStain = labStains[w.order?.lab_stain_id || 0];
    const orderStain = stains[labStain?.stain_id || 1];

    const orderStainId = orderStain.id;
    if (orderStain.category === "ROUTINE") {
      pathCase.blocks.filter(blockFilter(w, pathCase)).forEach((block) => {
        if (!block.id) return;

        if (!(orderStainId in routineBlocksOrderByStain))
          routineBlocksOrderByStain[orderStain.id] = { blockIds: [] };

        routineBlocksOrderByStain[orderStain.id].blockIds.push(block.id);
        routineBlocksOrderByStain[orderStain.id].order = w.order;
      });
      routineWork.push(w);
    } else {
      nonRoutineWork.push(w);
    }
  });

  nonRoutineWork.sort(workProcessSort);

  const numBlocksToCut = blocks.length;
  let label = `${numBlocksToCut} ${pluralize("Block", numBlocksToCut)}`;

  const difSpecimenCount = pathCase.specimens.filter((s) =>
    // @ts-ignore
    s.protocols?.includes("dif")
  ).length;
  if (difSpecimenCount)
    label += ` & ${difSpecimenCount} DIF Specimen${
      difSpecimenCount === 1 ? "" : "s"
    }`;

  const slidesNotScannedOut = pathCase?.slides?.filter((s) => {
    const stain = stains[s.stain_id || 0];
    const isUnstained =
      stain?.abbreviation === "UNSTN" || stain?.name === "Unstained";
    return !isUnstained && s.lis_state === LISSlide.lis_state.UNSTAINED;
  });

  return filterCase ? (
    <CaseListItem
      id={pathCase.id}
      label={label}
      warning={
        !!slidesNotScannedOut?.length ? (
          <>
            <ExclamationCircleIcon height={"1.25em"} />
            {slidesNotScannedOut
              .map((s) => labelForSlide(s, pathCase, stains[s.stain_id || 0]))
              .join(", ")}{" "}
            {slidesNotScannedOut.length > 1 ? "have" : "has"} not been scanned
            out
          </>
        ) : undefined
      }
    >
      {Object.entries(routineBlocksOrderByStain).map(
        ([stainId, { blockIds, order }]) => (
          <RoutineWork
            key={stainId}
            order={order}
            pathCase={pathCase}
            stainId={parseInt(stainId)}
            blockIds={blockIds}
            onScanBlock={onScanBlock}
          />
        )
      )}
      {nonRoutineWork.map((w) => (
        <Order key={w.id} work={w} onScanBlock={onScanBlock} />
      ))}
    </CaseListItem>
  ) : null;
};

const CuttingStationList = () => {
  const selectedLabStation = useSelectedLabStation();

  const cases = useAppSelector(workWithTaskIn("cutting"));
  const staining = useAppSelector(workWithTaskIn(["staining", "scanning"]));
  const allowIncompleteCutScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "allowIncompleteCutScanning")
  );
  const allowSlideScanOutFromList = useAppSelector((state) =>
    selectFeatureFlag(state, "allowSlideScanOutFromList")
  );

  const { onScanBlock, onScanSlide } = useCuttingOnScanCallbacks();

  useBarcodeScanner(({ object_type, object_id }) => {
    if (object_type === "block") {
      onScanBlock(object_id);
    } else if (
      (allowIncompleteCutScanning || allowSlideScanOutFromList) &&
      object_type === "slide"
    ) {
      onScanSlide(object_id);
    } else {
      return false;
    }
  });

  const [category, setCategory] = useState<Stain.category>();
  const [filterBySendout, setFilterBySendout] = useState<string>("");

  // Check the session for filters on mount
  useState(() => {
    const cuttingOrderFilter = localStorage.getItem("cuttingOrderFilter");
    const cuttingCategoryFilter = localStorage.getItem("cuttingCategoryFilter");

    if (cuttingOrderFilter) {
      setFilterBySendout(cuttingOrderFilter);
    }
    if (cuttingCategoryFilter) {
      setCategory(cuttingCategoryFilter as Stain.category);
    }
  });

  const resetFilters = () => {
    setFilterBySendout("");
    setCategory(undefined);
    localStorage.removeItem("cuttingOrderFilter");
    localStorage.removeItem("cuttingCategoryFilter");
  };

  const casesReady = useRequestAfterEverySiteLoad(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  return (
    <div className="Station Split">
      <main className="Cutting">
        <header className="Header Header--cutting">
          <figure className="Logo">
            <img src="/logo.svg" alt="" />
          </figure>
          <p>{selectedLabStation.name}</p>
          <div className="WorkListFilters">
            <select
              value={filterBySendout}
              onChange={(e) => {
                setFilterBySendout(e.target.value);
                localStorage.setItem("cuttingOrderFilter", e.target.value);
              }}
            >
              <option value={""}>All Orders</option>
              <option value={"inhouse"}>In-house Orders</option>
              <option value={"sendout"}>Sendout Orders</option>
            </select>
            <select
              value={category || ""}
              onChange={(e) => {
                setCategory(e.target.value as Stain.category);
                localStorage.setItem("cuttingCategoryFilter", e.target.value);
              }}
            >
              <option value={""}>All Categories</option>
              {Object.values(Stain.category)
                .filter((c) => !(c.startsWith("PW-") || c.startsWith("CED-")))
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </select>
            <button className="Button Button--primary" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
          <TaskCount cases={cases} stationType={selectedLabStation.type} />
        </header>

        <div className="WorkList WorkList--cutting">
          {cases.map((c) => (
            <Case
              key={c.id}
              pathCase={c}
              onScanBlock={onScanBlock}
              category={category}
              filterBySendout={filterBySendout}
            />
          ))}
        </div>
      </main>

      <aside>
        <h4>Ready for Staining</h4>
        {staining.map((c) => (
          <CaseReadyItem key={c.id} readyCase={c} />
        ))}
      </aside>
    </div>
  );
};

export default CuttingStationList;
